// src/pages/Dashboard.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

function Dashboard() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalLetters: 0,
    totalMemories: 0,
    recentPosts: [],
    recentLetters: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }

    const fetchDashboardData = async () => {
      try {
        // 통계 데이터 가져오기
        const [postsResult, lettersResult, memoriesResult, recentPostsResult, recentLettersResult] =
          await Promise.all([
            supabase.from('blog_posts').select('id', { count: 'exact', head: true }),
            supabase.from('letters').select('id', { count: 'exact', head: true }),
            supabase.from('memories').select('id', { count: 'exact', head: true }),
            supabase
              .from('blog_posts')
              .select('id, title, slug, created_at, category')
              .order('created_at', { ascending: false })
              .limit(5),
            supabase
              .from('letters')
              .select('id, name, message, created_at, is_private')
              .order('created_at', { ascending: false })
              .limit(5),
          ]);

        setStats({
          totalPosts: postsResult.count || 0,
          totalLetters: lettersResult.count || 0,
          totalMemories: memoriesResult.count || 0,
          recentPosts: recentPostsResult.data || [],
          recentLetters: recentLettersResult.data || [],
        });
      } catch (error) {
        console.error('대시보드 데이터 로딩 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isAdmin, navigate]);

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-loading">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">관리자 대시보드</h1>
        <p className="dashboard-subtitle">포트폴리오 관리 현황을 한눈에 확인하세요</p>
      </div>

      {/* 통계 카드 */}
      <div className="stats-grid">
        <div className="stat-card stat-card-posts">
          <div className="stat-icon">📝</div>
          <div className="stat-content">
            <h3 className="stat-label">블로그 포스트</h3>
            <p className="stat-value">{stats.totalPosts}</p>
          </div>
          <Link to="/blog" className="stat-link">
            관리하기 →
          </Link>
        </div>

        <div className="stat-card stat-card-letters">
          <div className="stat-icon">✉️</div>
          <div className="stat-content">
            <h3 className="stat-label">편지</h3>
            <p className="stat-value">{stats.totalLetters}</p>
          </div>
          <Link to="/admin-letters" className="stat-link">
            관리하기 →
          </Link>
        </div>

        <div className="stat-card stat-card-memories">
          <div className="stat-icon">🖼️</div>
          <div className="stat-content">
            <h3 className="stat-label">추억</h3>
            <p className="stat-value">{stats.totalMemories}</p>
          </div>
          <Link to="/memories" className="stat-link">
            관리하기 →
          </Link>
        </div>
      </div>

      {/* 빠른 작업 */}
      <div className="quick-actions">
        <h2 className="section-title">빠른 작업</h2>
        <div className="action-buttons">
          <button className="action-btn primary" onClick={() => navigate('/blog/new')}>
            ✏️ 새 글 작성
          </button>
          <button className="action-btn" onClick={() => navigate('/admin-letters')}>
            ✉️ 편지 관리
          </button>
          <button className="action-btn" onClick={() => navigate('/memories')}>
            🖼️ 추억 관리
          </button>
          <button className="action-btn ghost" onClick={() => navigate('/')}>
            🏠 홈으로 가기
          </button>
        </div>
      </div>

      {/* 최근 활동 */}
      <div className="recent-activity">
        <div className="activity-section">
          <h2 className="section-title">최근 블로그 포스트</h2>
          {stats.recentPosts.length === 0 ? (
            <p className="empty-state">작성된 포스트가 없습니다.</p>
          ) : (
            <ul className="activity-list">
              {stats.recentPosts.map((post) => (
                <li key={post.id} className="activity-item">
                  <div className="activity-link-wrapper">
                    <Link to={`/blog/${post.slug}`} className="activity-link">
                      <div className="activity-content">
                        <h4 className="activity-title">{post.title}</h4>
                        <div className="activity-meta">
                          <span className="activity-category">{post.category || '기타'}</span>
                          <span className="activity-date">
                            {new Date(post.created_at).toLocaleDateString('ko-KR')}
                          </span>
                        </div>
                      </div>
                    </Link>
                    <div className="activity-actions">
                      <button
                        className="icon-btn"
                        onClick={() => navigate(`/blog/edit/${post.slug}`)}
                        title="수정"
                      >
                        ✏️
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="activity-section">
          <h2 className="section-title">최근 편지</h2>
          {stats.recentLetters.length === 0 ? (
            <p className="empty-state">받은 편지가 없습니다.</p>
          ) : (
            <ul className="activity-list">
              {stats.recentLetters.map((letter) => (
                <li key={letter.id} className="activity-item">
                  <div className="activity-link">
                    <div className="activity-content">
                      <h4 className="activity-title">
                        {letter.name || '익명'}의 편지
                        {letter.is_private && <span className="private-badge">비공개</span>}
                      </h4>
                      <p className="activity-message">{letter.message.substring(0, 50)}...</p>
                      <span className="activity-date">
                        {new Date(letter.created_at).toLocaleDateString('ko-KR')}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
